import { trustedBySection } from './shared';
import type {
  FaqContent,
  FeatureBlockContent,
  HeroContent,
  Stat,
  TextSectionContent,
} from './types';

export const cardsHero: HeroContent = {
  title: 'Pay in USD with\nthe nsave card',
  cta: { label: 'Get the app', href: '/get-the-app' },
  image: {
    src: '/images/cards/hero.jpg',
    webpSrc: '/images/cards/hero.webp',
    alt: 'Hand holding a phone showing the nsave app with a red nsave Visa card on a breakfast table',
  },
};

export const cardsStats: Stat[] = [
  { icon: 'star', value: '4.4 ★', label: 'On the App Store' },
  { icon: 'users', value: '500k', label: 'nsave users' },
  { icon: 'at-sign', value: '250k', label: 'Community Members' },
  { icon: 'globe', value: '175m', label: 'Visa merchant locations' },
  { icon: 'bank', value: '1m+', label: 'Transactions' },
];

export const cardsTrustedBySection: TextSectionContent = {
  ...trustedBySection,
  body: 'We are on a mission to make global money movement secure, transparent, and accessible for everyone. Pay for the tools and services you need, wherever Visa is accepted. Set up your card in minutes and manage it from the app.',
  link: undefined,
};

export const cardsSpend: FeatureBlockContent = {
  title: 'Spend',
  image: {
    src: '/images/cards/spend.jpg',
    webpSrc: '/images/cards/spend.webp',
    alt: 'Person holding a phone displaying the nsave Visa card beside a breakfast bowl and orange juice',
  },
  rows: [
    {
      icon: 'globe',
      title: 'Wherever Visa is accepted',
      description:
        'Use your nsave card wherever Visa is accepted, across 200+ countries and territories at over 175 million locations worldwide',
      link: { label: 'Try the calculator', href: '/send-money-home' },
    },
    {
      icon: 'percent',
      title: 'No monthly fees',
      description:
        'No monthly, receiving, or top-up fees. A small currency conversion fee applies for spending outside your account currency.',
    },
    {
      icon: 'card',
      title: 'Virtual nsave card',
      description: 'Easily access your card from the nsave app.',
    },
  ],
};

export const cardsPayInATap: FeatureBlockContent = {
  title: 'Pay in a tap',
  image: {
    src: '/images/cards/pay-in-a-tap.jpg',
    webpSrc: '/images/cards/pay-in-a-tap.webp',
    alt: 'Tapping a phone with the nsave card to a contactless reader at a fruit market stall',
  },
  rows: [
    {
      icon: 'phone',
      logo: '/logos/apple.svg',
      title: 'Add to Apple Pay',
      description:
        'Your nsave card lives in your iPhone or Apple Watch. Tap to pay anywhere contactless is accepted.',
    },
    {
      icon: 'wallet',
      logo: '/logos/google.svg',
      title: 'Add to Google Wallet',
      description:
        'Android user? Add your card in seconds and start paying with your phone straight away.',
    },
  ],
};

export const cardsControl: FeatureBlockContent = {
  title: 'Control',
  image: {
    src: '/images/cards/control.jpg',
    webpSrc: '/images/cards/control.webp',
    alt: 'A couple at a café reviewing their card settings together in the nsave app',
  },
  rows: [
    {
      icon: 'snowflake',
      title: 'Freeze & unfreeze instantly',
      description: "One tap to lock your card, one tap to bring it back. You're always in control.",
    },
    {
      icon: 'bell',
      title: 'Real-time spending alerts',
      description:
        "Get notified the moment a transaction hits. Always know what's happening with your money.",
    },
    {
      icon: 'help',
      title: 'Get 24/7 support',
      description: "Reach our team any time, day or night, whether you're at home or abroad.",
    },
  ],
};

export const cardsFaq: FaqContent = {
  title: 'Frequently asked Questions',
  items: [
    {
      question: 'Why was my card payment declined?',
      answer:
        'A card payment can be declined if your card is frozen, your balance is too low, or the merchant cannot accept the transaction. Check the app for details or contact support.',
    },
    {
      question: 'When will I receive my card?',
      answer:
        'Your virtual card will appear in the nsave app once it is ready. Any physical-card delivery timing depends on your location and card status.',
    },
    {
      question: 'Why is my card payment showing as processing?',
      answer:
        'A processing payment is still being confirmed by the merchant. It will update automatically when the merchant completes or releases it.',
    },
    {
      question: 'How do I add my card to Apple or Google wallet?',
      answer:
        'Open your card in the nsave app, choose the option to add it to Apple Pay or Google Wallet, and follow the wallet instructions.',
    },
    {
      question: 'Which currencies can I pay with?',
      answer:
        'You can pay in any currency wherever Visa is accepted. Currency conversion applies when the payment currency differs from your account currency.',
    },
    {
      question: 'How do I add money to my account?',
      answer:
        'Use one of the funding options shown in your nsave app. Available methods depend on your account and country.',
    },
  ],
};
